if (!customElements.get("variant-selects-orchestrator")) {
  customElements.define(
    "variant-selects-orchestrator",
    class VariantSelectsOrchestrator extends HTMLElement {
      constructor() {
        super();
        this.childVariantSelects = [];
        this.isUpdating = false;
        this.originalPublishMethods = new Map();
      }

      connectedCallback() {
        // Attendre que les enfants variant-selects soient prêts
        setTimeout(() => {
          this.initializeChildComponents();
        }, 100);

        // Écouter les changements des composants enfants
        this.addEventListener(
          "change",
          this.handleChildChange.bind(this),
          true,
        ); // Capture phase
      }

      initializeChildComponents() {
        // Trouver tous les composants variant-selects enfants
        this.childVariantSelects = Array.from(
          this.querySelectorAll("variant-selects"),
        );

        // Désactiver les publications d'événements des enfants
        this.childVariantSelects.forEach((child) => {
          this.disableChildEventPublishing(child);
        });
      }

      disableChildEventPublishing(childComponent) {
        // Supprimer tous les event listeners 'change' existants
        const newChild = childComponent.cloneNode(true);
        childComponent.parentNode.replaceChild(newChild, childComponent);

        // Réattacher seulement updateSelectedSwatchValue
        newChild.updateSelectedSwatchValue =
          childComponent.updateSelectedSwatchValue?.bind(newChild);
        newChild.getInputForEventTarget =
          childComponent.getInputForEventTarget?.bind(newChild);

        // Mettre à jour notre référence
        const index = this.childVariantSelects.indexOf(childComponent);
        if (index > -1) {
          this.childVariantSelects[index] = newChild;
        }
      }

      handleChildChange(event) {
        // Vérifier que l'événement vient bien d'un de nos enfants
        const isFromOurChild = this.childVariantSelects.some((child) =>
          child.contains(event.target),
        );

        if (!isFromOurChild) return;

        // Éviter les boucles infinies
        if (this.isUpdating) {
          event.stopImmediatePropagation();
          event.preventDefault();
          return;
        }

        // Empêcher la propagation immédiatement
        event.stopImmediatePropagation();
        event.preventDefault();

        this.isUpdating = true;

        // Utiliser requestAnimationFrame pour éviter les conflits
        requestAnimationFrame(() => {
          try {
            const target = this.getInputForEventTarget(event.target);

            // S'assurer que le target a un ID valide
            if (!target.id) {
              target.id = `variant-option-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            }

            // Créer un événement synthétique sans propagation
            const syntheticEvent = {
              target: event.target,
              type: event.type,
              stopImmediatePropagation: () => {},
              preventDefault: () => {},
            };

            // Mettre à jour l'affichage des swatches SANS déclencher d'événements
            const parentVariantSelect = event.target.closest("variant-selects");
            if (
              parentVariantSelect &&
              typeof parentVariantSelect.updateSelectedSwatchValue ===
                "function"
            ) {
              parentVariantSelect.updateSelectedSwatchValue(syntheticEvent);
            }

            // Déclencher le recalcul des prix
            this.updatePricing(event.target);

            // Collecter TOUTES les valeurs sélectionnées de TOUS les composants enfants
            const allSelectedOptionValues = this.getAllSelectedOptionValues();

            // Publier l'événement global avec toutes les valeurs
            FoxTheme.pubsub.publish(
              FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
              {
                data: {
                  event: syntheticEvent,
                  target,
                  selectedOptionValues: allSelectedOptionValues,
                },
              },
            );

            // Calculer la variante correspondante
            const selectedVariant = this.calculateSelectedVariant(
              allSelectedOptionValues,
            );

            if (selectedVariant) {
              // Publier l'événement variantChange
              FoxTheme.pubsub.publish(
                FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
                {
                  data: {
                    sectionId: this.sectionId || "main-product",
                    variant: selectedVariant,
                    selectedOptionValues: allSelectedOptionValues,
                  },
                },
              );

              // Publier également l'événement DOM personnalisé
              document.dispatchEvent(
                new CustomEvent("variant:changed", {
                  detail: {
                    variant: selectedVariant,
                    orchestrator: this,
                  },
                }),
              );
            }
          } finally {
            // Remettre isUpdating à false après un délai plus long
            setTimeout(() => {
              this.isUpdating = false;
            }, 200);
          }
        });
      }

      getInputForEventTarget(target) {
        return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
      }

      getAllSelectedOptionValues() {
        const allValues = [];

        this.childVariantSelects.forEach((child) => {
          // Utiliser la méthode getter existante de chaque composant
          const childValues = Array.from(
            child.querySelectorAll(
              "select option[selected], fieldset input:checked",
            ),
          ).map(({ dataset }) => dataset.optionValueId);

          allValues.push(...childValues);
        });

        return allValues.filter(Boolean); // Filtrer les valeurs undefined/null
      }

      // Méthode pour mettre à jour les composants enfants lors de la réponse serveur
      updateChildComponents(newHtml) {
        const newOrchestrator = newHtml.querySelector(
          "variant-selects-orchestrator",
        );
        if (!newOrchestrator) return;

        // Temporairement désactiver les mises à jour
        this.isUpdating = true;

        try {
          // Mettre à jour chaque composant enfant individuellement
          this.childVariantSelects.forEach((child, index) => {
            const newChild = newOrchestrator.children[index];
            if (newChild && newChild.tagName === "VARIANT-SELECTS") {
              // Utiliser notre méthode optimisée pour éviter le scintillement des images
              this.updateChildWithImageOptimization(child, newChild);
            }
          });

          // Réinitialiser les composants enfants
          setTimeout(() => {
            this.initializeChildComponents();
            // CORRECTION : Utiliser la nouvelle méthode de mise à jour des prix
            this.updatePricing();
            this.isUpdating = false;
          }, 300);
        } catch (error) {
          console.error(
            "Erreur lors de la mise à jour des composants enfants:",
            error,
          );
          this.isUpdating = false;
        }
      }

      // Nouvelle méthode pour mettre à jour le contenu en préservant les images identiques
      updateChildWithImageOptimization(oldChild, newChild) {
        // Si HTMLUpdateUtility est disponible, l'utiliser (il gère déjà les optimisations)
        if (window.HTMLUpdateUtility) {
          HTMLUpdateUtility.viewTransition(oldChild, newChild);
          return;
        }

        // Sinon, utiliser notre logique personnalisée
        this.smartUpdateContent(oldChild, newChild);
      }

      // Méthode pour mise à jour intelligente du contenu
      smartUpdateContent(oldElement, newElement) {
        // Récupérer toutes les images existantes avec leurs sources
        const oldImages = new Map();
        const oldImageElements = oldElement.querySelectorAll('.item-image img');
        
        oldImageElements.forEach((img, index) => {
          const src = img.src || img.getAttribute('src');
          if (src) {
            oldImages.set(src, {
              element: img,
              parent: img.closest('.item-image'),
              index: index
            });
          }
        });

        // Récupérer les nouvelles images
        const newImages = new Map();
        const newImageElements = newElement.querySelectorAll('.item-image img');
        
        newImageElements.forEach((img, index) => {
          const src = img.src || img.getAttribute('src');
          if (src) {
            newImages.set(src, {
              element: img,
              parent: img.closest('.item-image'),
              index: index
            });
          }
        });

        // Effectuer la mise à jour normale du contenu
        oldElement.innerHTML = newElement.innerHTML;

        // Après la mise à jour, restaurer les images qui n'ont pas changé
        const updatedImageElements = oldElement.querySelectorAll('.item-image img');
        
        updatedImageElements.forEach((newImg, index) => {
          const newSrc = newImg.src || newImg.getAttribute('src');
          
          if (newSrc && oldImages.has(newSrc)) {
            const oldImageData = oldImages.get(newSrc);
            
            // Si l'image existait déjà avec la même source, la remplacer par l'ancienne
            // pour éviter le rechargement et le scintillement
            if (oldImageData.element && oldImageData.element.complete) {
              // Copier les attributs importants de la nouvelle image
              const newClasses = newImg.className;
              const newAlt = newImg.alt;
              const newStyle = newImg.style.cssText;
              
              // Remplacer par l'ancienne image mais avec les nouveaux attributs
              const preservedImg = oldImageData.element.cloneNode(true);
              preservedImg.className = newClasses;
              preservedImg.alt = newAlt;
              preservedImg.style.cssText = newStyle;
              
              // Remplacer dans le DOM
              newImg.parentNode.replaceChild(preservedImg, newImg);
              
              console.log(`🖼️ Image préservée pour éviter le scintillement: ${newSrc}`);
            }
          }
        });
      }

      // Les prix sont mis à jour par le configurateur (src/lib/ign/ui/variant-pricing.ts),
      // qui écoute lui-même les changements de variante : plus rien à faire ici.
      updatePricing(changedInput) {}

      // Supprimer complètement la méthode manualPriceUpdate et toutes les méthodes complexes de calcul de prix

      // Nouvelle méthode à ajouter dans la classe
      calculateSelectedVariant(selectedOptionValues) {
        // Récupérer les données des variantes
        const variantData = document.querySelector("[data-product-variants]");
        if (!variantData) {
          console.error("❌ Aucun élément [data-product-variants] trouvé");
          return null;
        }

        const variants = JSON.parse(variantData.textContent);

        // Chercher la variante correspondante
        const matchingVariant = variants.find((variant) => {
          // Vérifier la correspondance exacte Taille + Matière
          const tailleMatch =
            variant.option1 ===
            (selectedOptionValues.Taille || selectedOptionValues.taille);
          const matiereMatch =
            variant.option2 ===
            (selectedOptionValues.Matière || selectedOptionValues.matiere);

          const matches = tailleMatch && matiereMatch;

          return matches;
        });

        return matchingVariant;
      }
    },
  );
}
